// src/pages/calculation/ImportORModal.jsx
import React, { useRef, useState } from "react";
import styles from "./pre_calculation.module.css";
import AuthService from "../../services/AuthService";

const ImportORModal = ({ open, onClose, onResult, onUseData }) => {
  if (!open) return null;

  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // ========= helpers de mapeamento =========
  const normalize = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // tira acentos
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")     // << remove :, /, ., etc
      .replace(/\s+/g, " ")
      .trim();
  const toInt = (val) => {
    const digits = String(val ?? "").replace(/\D/g, "");
    return digits ? String(parseInt(digits, 10)) : "";
  };

  const toCurrencyBR = (val) => {
    const digits = String(val || "").replace(/\D/g, "");
    if (!digits) return "";
    const cents = parseInt(digits, 10) / 100;
    return cents
      .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
      .replace(/\u00A0/g, " ");
  };

  const _strip = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const coerceBoolean = (val) => {
    if (typeof val === "boolean") return val;
    const s = _strip(val);
    if (["1", "true", "sim", "s", "yes", "y"].includes(s)) return true;
    if (["0", "false", "nao", "não", "n", "no"].includes(s)) return false;
    return null; // desconhecido
  };
  const boolToSimNao = (b) => (b ? "SIM" : "NAO");

  const buildPrefillFromResult = (raw) => {
    const map = {};
    Object.entries(raw || {}).forEach(([k, v]) => {
      map[normalize(k)] = typeof v === "string" ? v.trim() : v; // preserva bool/number
    });

    const get = (...keys) => {
      for (const k of keys) {
        const val = map[normalize(k)];
        // permite retornar boolean/number diretamente
        if (val === 0 || typeof val === "number" || typeof val === "boolean") return val;
        if (val != null && String(val).trim() !== "") return String(val).trim();
      }
      return "";
    };

    const rraCountRaw = get(
      "RESULTADO RRA", "RESULTADO RRA :", "RRA", "RRA (MESES)",
      "CONTAGEM RRA", "QTD RRA", "QUANTIDADE RRA", "QUANTIDADE DE MESES RRA"
    );
    const contagemRRA = toInt(rraCountRaw);

    // lê "decimo terceiro": true/false e aplica fallback para NAO
    const dtBool = coerceBoolean(map["decimo terceiro"]);
    const dtSN   = boolToSimNao(dtBool === null ? false : dtBool); // se não vier -> "NAO"

    return {
      numero_processo: get("Numero do Processo", "Número do Processo", "Processo"),
      nome: get("Nome"),
      data_base: get("DATA BASE", "Data base"),
      data_oficio: get("DATA OFICIO", "Data do ofício", "Data do oficio"),
      data_atualizacao: get("DATA ATUALIZACAO", "Data de atualização", "Data atualizacao") || "",

      valor_principal:      toCurrencyBR(get("PRINCIPAL", "Valor principal")),
      valor_moratorio:      toCurrencyBR(get("juros moratorio", "Moratório", "Juros moratórios")),
      valor_assistencia:    toCurrencyBR(get("assistencia medica", "Assistencia medica", "Assistência médica")),
      valor_previdenciario: toCurrencyBR(get("desconto previdenciario", "Desconto previdenciario")),

      // rra em UM único objeto:
      rra: {
        codigo_o_c: get("O.C.", "O C", "OC", "O/C"),
        contagem_meses: contagemRRA,
        decimo_terceiro: dtSN, // "SIM" | "NAO"
      },
      // honorarios/municipal_estadual ficam para o usuário
    };
  };
  // ========================================

  const pickFile = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      setError("Arquivo inválido. Envie um PDF.");
      setFile(null);
      return;
    }
    setError("");
    setFile(f);
  };

  const onInputChange = (e) => pickFile(e.target.files?.[0] || null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Selecione um arquivo PDF."); return; }

    try {
      setUploading(true);
      const resp = await AuthService.readPdf(file);
      const data = resp?.data ?? resp;

      // opcional: para depuração
      onResult?.(data);

      // monta o prefill e navega direto para o formulário
      const prefill = buildPrefillFromResult(data);
      if (onUseData) onUseData(prefill);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.message || "Erro ao processar o PDF.";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const sizeKB = file ? (file.size / 1024).toFixed(1) : null;

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="import-or-title">
      <div className={styles.modalCard}>
        <h3 id="import-or-title" className={styles.modalTitle}>Importar OR (PDF)</h3>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* INPUT escondido */}
          <input
            id="or-pdf-input"
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => {
              onInputChange(e);
              e.target.value = ""; // permite re-selecionar o mesmo arquivo
            }}
            style={{ display: "none" }}
          />

          {/* DROPZONE */}
          <label
            htmlFor="or-pdf-input"
            className={`${styles.dropzone} ${dragActive ? styles.dragActive : ""}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setDragActive(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
          >
            <div>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>
              <strong>Escolha um arquivo ou arraste o PDF aqui</strong>
              <div className={styles.helper}>clique para selecionar</div>
            </div>
          </label>

          {file && (
            <div className={styles.fileMeta}>
              <strong>Selecionado:</strong> {file.name} {sizeKB && `(${sizeKB} KB)`}
            </div>
          )}

          {error && <p className={styles.errorMessage}>{error}</p>}

          <div className={styles.modalFooterCentered}>
            <button type="button" className={styles.backButton} onClick={onClose}>Fechar</button>
            <button type="submit" disabled={uploading || !file}>
              {uploading ? "Enviando..." : "Importar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportORModal;
